<div class="qc-drawer-root" *ngIf="open">
  <div
    class="qc-scrim"
    role="presentation"
    (click)="emitClose()"
    aria-hidden="true"
  ></div>

  <div
    class="qc-panel"
    role="dialog"
    aria-modal="true"
    aria-labelledby="qc-drawer-title"
    (click)="$event.stopPropagation()"
  >
    <header class="qc-header">
      <h2 id="qc-drawer-title" class="qc-title">Quick compare</h2>
      <button type="button" class="qc-close btn-secondary" (click)="emitClose()" aria-label="Close">
        Close
      </button>
    </header>

    <div class="qc-columns">
      <ng-container *ngIf="bothColumnsCompleted; else qcStandardColumns">
        <div class="qc-paired-outer">
          <div class="qc-paired-headers">
            <section class="qc-col qc-col-head-only" [attr.aria-label]="primaryDomainLabel || 'Your site'">
              <h3 class="qc-col-label">{{ primaryDomainLabel || 'Your site' }}</h3>
              <a class="qc-url qc-url-link" [href]="primaryUrl" target="_blank" rel="noopener noreferrer">{{
                primaryUrl
              }}</a>
            </section>
            <section class="qc-col qc-col-head-only" [attr.aria-label]="compareDomainLabel || 'Compare to'">
              <h3 class="qc-col-label">{{ compareDomainLabel || 'Compare to' }}</h3>
              <a class="qc-url qc-url-link" [href]="compareUrl" target="_blank" rel="noopener noreferrer">{{
                compareUrl
              }}</a>
            </section>
          </div>
          <div class="qc-paired-sync">
            <div class="qc-sync-cell">
              <p class="qc-metric-title">{{ pageTitle(primaryColumn.analysis) }}</p>
            </div>
            <div class="qc-sync-cell">
              <p class="qc-metric-title">{{ pageTitle(compareColumn.analysis) }}</p>
            </div>
            <div class="qc-sync-cell">
              <dl class="qc-dl">
                <dt>On-page score</dt>
                <dd>{{ primaryColumn.analysis.onpageScore ?? '—' }}</dd>
                <dt>Word count</dt>
                <dd>{{ primaryColumn.analysis.wordCount ?? '—' }}</dd>
                <dt>Est. traffic / mo</dt>
                <dd>{{ formatEstTraffic(primaryColumn.analysis) }}</dd>
                <dt>Ranked keywords (in DB)</dt>
                <dd>
                  {{ rankedKeywordDisplayCount(primaryColumn.analysis) }}
                  <p class="qc-metric-note" *ngIf="rankedKeywordsMissingNote(primaryColumn.analysis)">
                    {{ keywordFetchHint }}
                  </p>
                </dd>
              </dl>
            </div>
            <div class="qc-sync-cell">
              <dl class="qc-dl">
                <dt>On-page score</dt>
                <dd>{{ compareColumn.analysis.onpageScore ?? '—' }}</dd>
                <dt>Word count</dt>
                <dd>{{ compareColumn.analysis.wordCount ?? '—' }}</dd>
                <dt>Est. traffic / mo</dt>
                <dd>{{ formatEstTraffic(compareColumn.analysis) }}</dd>
                <dt>Ranked keywords (in DB)</dt>
                <dd>
                  {{ rankedKeywordDisplayCount(compareColumn.analysis) }}
                  <p class="qc-metric-note" *ngIf="rankedKeywordsMissingNote(compareColumn.analysis)">
                    {{ keywordFetchHint }}
                  </p>
                </dd>
              </dl>
            </div>
            <div class="qc-sync-cell">
              <ng-container *ngTemplateOutlet="qcTopicFitBlock; context: { side: 'primary' }"></ng-container>
            </div>
            <div class="qc-sync-cell">
              <ng-container *ngTemplateOutlet="qcTopicFitBlock; context: { side: 'compare' }"></ng-container>
            </div>
            <div class="qc-sync-cell">
              <ng-container *ngTemplateOutlet="qcIncomingBlock; context: { side: 'primary' }"></ng-container>
            </div>
            <div class="qc-sync-cell">
              <ng-container *ngTemplateOutlet="qcIncomingBlock; context: { side: 'compare' }"></ng-container>
            </div>
            <div class="qc-sync-cell">
              <ng-container *ngTemplateOutlet="qcFreshnessBlock; context: { side: 'primary' }"></ng-container>
            </div>
            <div class="qc-sync-cell">
              <ng-container *ngTemplateOutlet="qcFreshnessBlock; context: { side: 'compare' }"></ng-container>
            </div>
            <div class="qc-sync-cell">
              <ng-container *ngTemplateOutlet="qcIssuesBlock; context: { side: 'primary' }"></ng-container>
            </div>
            <div class="qc-sync-cell">
              <ng-container *ngTemplateOutlet="qcIssuesBlock; context: { side: 'compare' }"></ng-container>
            </div>
          </div>
        </div>
      </ng-container>

      <ng-template #qcStandardColumns>
        <section class="qc-col" [attr.aria-label]="primaryDomainLabel || 'Your site'">
          <h3 class="qc-col-label">{{ primaryDomainLabel || 'Your site' }}</h3>
          <a class="qc-url qc-url-link" [href]="primaryUrl" target="_blank" rel="noopener noreferrer">{{
            primaryUrl
          }}</a>

          <div class="qc-state" *ngIf="primaryColumn.status === 'loading'">
            <div class="loading-spinner loading-spinner-sm"></div>
            <span>Loading analysis…</span>
          </div>

          <div class="qc-state qc-analyzing" *ngIf="primaryColumn.status === 'analyzing'">
            <div class="loading-spinner loading-spinner-sm"></div>
            <span>Analysis in progress…</span>
            <p class="qc-progress" *ngIf="primaryColumn.progress">
              Crawled {{ primaryColumn.progress.pagesCrawled }} · Queue {{ primaryColumn.progress.pagesInQueue }}
            </p>
          </div>

          <div class="qc-state qc-error" *ngIf="primaryColumn.status === 'error'">
            <p>{{ primaryColumn.error }}</p>
          </div>

          <div class="qc-state qc-empty" *ngIf="primaryColumn.status === 'empty'">
            <p>No analysis yet for this URL.</p>
          </div>

          <div class="qc-state qc-prompt" *ngIf="primaryColumn.status === 'prompt'">
            <p class="qc-prompt-msg">This URL hasn't been analyzed yet.</p>
            <button type="button" class="btn-secondary btn-sm qc-prompt-btn" (click)="confirmAnalysis('primary')">
              Analyze now
            </button>
          </div>

          <div class="qc-metrics" *ngIf="primaryColumn.status === 'completed' && primaryColumn.analysis">
            <p class="qc-metric-title">{{ pageTitle(primaryColumn.analysis) }}</p>
            <dl class="qc-dl">
              <dt>On-page score</dt>
              <dd>{{ primaryColumn.analysis.onpageScore ?? '—' }}</dd>
              <dt>Word count</dt>
              <dd>{{ primaryColumn.analysis.wordCount ?? '—' }}</dd>
              <dt>Est. traffic / mo</dt>
              <dd>{{ formatEstTraffic(primaryColumn.analysis) }}</dd>
              <dt>Ranked keywords (in DB)</dt>
              <dd>
                {{ rankedKeywordDisplayCount(primaryColumn.analysis) }}
                <p class="qc-metric-note" *ngIf="rankedKeywordsMissingNote(primaryColumn.analysis)">
                  {{ keywordFetchHint }}
                </p>
              </dd>
            </dl>

            <ng-container *ngTemplateOutlet="qcTopicFitBlock; context: { side: 'primary' }"></ng-container>

            <ng-container *ngTemplateOutlet="qcIncomingBlock; context: { side: 'primary' }"></ng-container>

            <ng-container *ngTemplateOutlet="qcFreshnessBlock; context: { side: 'primary' }"></ng-container>
            <ng-container *ngTemplateOutlet="qcIssuesBlock; context: { side: 'primary' }"></ng-container>
          </div>
        </section>

        <section class="qc-col" [attr.aria-label]="compareDomainLabel || 'Compare to'">
          <h3 class="qc-col-label">{{ compareDomainLabel || 'Compare to' }}</h3>
          <a class="qc-url qc-url-link" [href]="compareUrl" target="_blank" rel="noopener noreferrer">{{
            compareUrl
          }}</a>

          <div class="qc-state" *ngIf="compareColumn.status === 'loading'">
            <div class="loading-spinner loading-spinner-sm"></div>
            <span>Loading analysis…</span>
          </div>

          <div class="qc-state qc-analyzing" *ngIf="compareColumn.status === 'analyzing'">
            <div class="loading-spinner loading-spinner-sm"></div>
            <span>Analysis in progress…</span>
            <p class="qc-progress" *ngIf="compareColumn.progress">
              Crawled {{ compareColumn.progress.pagesCrawled }} · Queue {{ compareColumn.progress.pagesInQueue }}
            </p>
          </div>

          <div class="qc-state qc-error" *ngIf="compareColumn.status === 'error'">
            <p>{{ compareColumn.error }}</p>
          </div>

          <div class="qc-state qc-empty" *ngIf="compareColumn.status === 'empty'">
            <p>No analysis yet for this URL.</p>
          </div>

          <div class="qc-state qc-prompt" *ngIf="compareColumn.status === 'prompt'">
            <p class="qc-prompt-msg">This URL hasn't been analyzed yet.</p>
            <button type="button" class="btn-secondary btn-sm qc-prompt-btn" (click)="confirmAnalysis('compare')">
              Analyze now
            </button>
          </div>

          <div class="qc-metrics" *ngIf="compareColumn.status === 'completed' && compareColumn.analysis">
            <p class="qc-metric-title">{{ pageTitle(compareColumn.analysis) }}</p>
            <dl class="qc-dl">
              <dt>On-page score</dt>
              <dd>{{ compareColumn.analysis.onpageScore ?? '—' }}</dd>
              <dt>Word count</dt>
              <dd>{{ compareColumn.analysis.wordCount ?? '—' }}</dd>
              <dt>Est. traffic / mo</dt>
              <dd>{{ formatEstTraffic(compareColumn.analysis) }}</dd>
              <dt>Ranked keywords (in DB)</dt>
              <dd>
                {{ rankedKeywordDisplayCount(compareColumn.analysis) }}
                <p class="qc-metric-note" *ngIf="rankedKeywordsMissingNote(compareColumn.analysis)">
                  {{ keywordFetchHint }}
                </p>
              </dd>
            </dl>

            <ng-container *ngTemplateOutlet="qcTopicFitBlock; context: { side: 'compare' }"></ng-container>

            <ng-container *ngTemplateOutlet="qcIncomingBlock; context: { side: 'compare' }"></ng-container>

            <ng-container *ngTemplateOutlet="qcFreshnessBlock; context: { side: 'compare' }"></ng-container>
            <ng-container *ngTemplateOutlet="qcIssuesBlock; context: { side: 'compare' }"></ng-container>
          </div>
        </section>
      </ng-template>

      <!-- Full-width: keyword overlap, opportunities, competitive diagnosis (both sides loaded) -->
      <div class="qc-span-full" *ngIf="bothColumnsCompleted">
        <p class="qc-section-intro">{{ keywordPartialHint }}</p>

        <div class="qc-metric-card qc-overlap-card">
          <div class="metric-label">Keyword overlap and opportunities</div>
          <p class="qc-metric-note" *ngIf="showKeywordDependencyCallout">{{ keywordFetchHint }}</p>

          <ng-container *ngIf="computeKeywordOverlap() as overlap; else qcOverlapMissing">
            <div class="qc-overlap-summary">
              <div class="qc-overlap-stat qc-overlap-shared">
                <div class="qc-overlap-label">Shared keywords</div>
                <div class="qc-overlap-value">{{ overlap.sharedCount }}</div>
                <div class="qc-overlap-detail" *ngIf="overlap.sharedCount > 0">
                  Your avg pos: <strong>{{ overlap.sharedAvgPositionYour }}</strong> · Compare avg pos:
                  <strong>{{ overlap.sharedAvgPositionCompetitor }}</strong>
                </div>
              </div>
              <div class="qc-overlap-stat qc-overlap-yours">
                <div class="qc-overlap-label">Your unique</div>
                <div class="qc-overlap-value">{{ overlap.yourUniqueCount }}</div>
              </div>
              <div class="qc-overlap-stat qc-overlap-theirs">
                <div class="qc-overlap-label">Their unique</div>
                <div class="qc-overlap-value">{{ overlap.competitorUniqueCount }}</div>
              </div>
            </div>

            <div class="qc-opportunities" *ngIf="getKeywordOpportunities().length > 0">
              <button type="button" class="qc-dropdown-toggle qc-opportunities-toggle" (click)="toggleKeywordOpportunities()">
                <span class="qc-opportunities-icon">&#128161;</span>
                <span>{{ getKeywordOpportunities().length }} Content opportunities</span>
                <span class="qc-dropdown-arrow" [class.expanded]="isKeywordOpportunitiesExpanded()">&#9660;</span>
              </button>
              <div class="qc-opportunities-content" *ngIf="isKeywordOpportunitiesExpanded()">
                <p class="qc-metric-note">Competitor-only keywords with meaningful volume. {{ keywordPartialHint }}</p>
                <ul class="qc-opportunities-list">
                  <li *ngFor="let opp of getKeywordOpportunities()">
                    <span class="qc-opportunity-kw">{{ opp.keyword }}</span>
                    <span class="qc-opportunity-meta">Pos {{ opp.position }} · {{ opp.searchVolume | number }} vol</span>
                  </li>
                </ul>
              </div>
            </div>

            <div class="qc-keyword-details" *ngIf="overlap.sharedCount > 0 || overlap.yourUniqueCount > 0 || overlap.competitorUniqueCount > 0">
              <button type="button" class="qc-dropdown-toggle qc-opportunities-toggle" (click)="toggleKeywordDetails()">
                <span>Show exact ranking keywords and positions</span>
                <span class="qc-dropdown-arrow" [class.expanded]="isKeywordDetailsExpanded()">&#9660;</span>
              </button>
              <div class="qc-opportunities-content" *ngIf="isKeywordDetailsExpanded()">
                <div class="qc-keyword-details-grid">
                  <div class="qc-keyword-column">
                    <h4 class="qc-keyword-col-title">Shared</h4>
                    <ul class="qc-keyword-list" *ngIf="getSharedKeywordsWithPositions().length > 0; else qcNoShared">
                      <li *ngFor="let kw of getSharedKeywordsWithPositions()">
                        <span class="qc-opportunity-kw">{{ kw.keyword }}</span>
                        <span class="qc-opportunity-meta">You {{ kw.yourPosition }} · Them {{ kw.competitorPosition }}</span>
                      </li>
                    </ul>
                    <ng-template #qcNoShared>
                      <p class="qc-muted">No shared keywords in loaded dataset.</p>
                    </ng-template>
                  </div>

                  <div class="qc-keyword-column">
                    <h4 class="qc-keyword-col-title">Your unique</h4>
                    <ul class="qc-keyword-list" *ngIf="getPrimaryUniqueKeywords().length > 0; else qcNoPrimaryUnique">
                      <li *ngFor="let kw of getPrimaryUniqueKeywords()">
                        <span class="qc-opportunity-kw">{{ kw.keyword }}</span>
                        <span class="qc-opportunity-meta">Pos {{ kw.position }}</span>
                      </li>
                    </ul>
                    <ng-template #qcNoPrimaryUnique>
                      <p class="qc-muted">No unique keywords on your side in loaded dataset.</p>
                    </ng-template>
                  </div>

                  <div class="qc-keyword-column">
                    <h4 class="qc-keyword-col-title">Their unique</h4>
                    <ul class="qc-keyword-list" *ngIf="getCompareUniqueKeywords().length > 0; else qcNoCompareUnique">
                      <li *ngFor="let kw of getCompareUniqueKeywords()">
                        <span class="qc-opportunity-kw">{{ kw.keyword }}</span>
                        <span class="qc-opportunity-meta">Pos {{ kw.position }}</span>
                      </li>
                    </ul>
                    <ng-template #qcNoCompareUnique>
                      <p class="qc-muted">No unique keywords on compare side in loaded dataset.</p>
                    </ng-template>
                  </div>
                </div>
              </div>
            </div>
          </ng-container>
          <ng-template #qcOverlapMissing>
            <p class="qc-muted">Ranked keyword data is not in the database for one or both URLs yet. {{ keywordFetchHint }}</p>
          </ng-template>
        </div>

        <div
          class="qc-diagnosis-banner"
          [class.qc-diagnosis-competitor]="hasCompetitorAdvantagesInDiagnosis() && !hasYourAdvantagesInDiagnosis()"
          [class.qc-diagnosis-yours]="hasYourAdvantagesInDiagnosis() && !hasCompetitorAdvantagesInDiagnosis()"
          [class.qc-diagnosis-mixed]="hasCompetitorAdvantagesInDiagnosis() && hasYourAdvantagesInDiagnosis()"
        >
          <div class="qc-diagnosis-header" *ngIf="getDiagnosisBullets().length > 0">
            <span class="qc-diagnosis-icon" *ngIf="hasCompetitorAdvantagesInDiagnosis() && !hasYourAdvantagesInDiagnosis()">&#9888;&#65039;</span>
            <span class="qc-diagnosis-icon" *ngIf="hasYourAdvantagesInDiagnosis() && !hasCompetitorAdvantagesInDiagnosis()">&#10004;&#65039;</span>
            <span class="qc-diagnosis-icon" *ngIf="hasCompetitorAdvantagesInDiagnosis() && hasYourAdvantagesInDiagnosis()">&#128202;</span>
            <h3 class="qc-diagnosis-title" *ngIf="hasCompetitorAdvantagesInDiagnosis() && !hasYourAdvantagesInDiagnosis()">Why they may have an edge</h3>
            <h3 class="qc-diagnosis-title" *ngIf="hasYourAdvantagesInDiagnosis() && !hasCompetitorAdvantagesInDiagnosis()">Your competitive advantages</h3>
            <h3 class="qc-diagnosis-title" *ngIf="hasCompetitorAdvantagesInDiagnosis() && hasYourAdvantagesInDiagnosis()">Competitive snapshot</h3>
          </div>
          <h3 class="qc-diagnosis-title qc-diagnosis-title-only" *ngIf="getDiagnosisBullets().length === 0">Competitive advantages</h3>
          <ul class="qc-diagnosis-list" *ngIf="getDiagnosisBullets().length > 0">
            <li
              *ngFor="let b of getDiagnosisBullets()"
              [class.qc-bullet-competitor]="b.winner === 'competitor'"
              [class.qc-bullet-you]="b.winner === 'you'"
            >
              <span class="qc-bullet-icon">{{ b.winner === 'competitor' ? '&#8722;' : '+' }}</span>
              <span>{{ b.text }}</span>
            </li>
          </ul>
          <p class="qc-muted qc-diagnosis-empty" *ngIf="getDiagnosisBullets().length === 0">
            No strong advantages detected with the data loaded here. Run Deep Compare for fuller keyword and SERP context.
          </p>
          <p class="qc-metric-note" *ngIf="showKeywordDependencyCallout || getDiagnosisBullets().length === 0">
            {{ keywordPartialHint }}
          </p>
        </div>

        <!-- Supporting pages by topic -->
        <div class="qc-metric-card qc-supporting-pages-card" *ngIf="supportingPages.status !== 'idle'">
          <button type="button" class="qc-dropdown-toggle qc-supporting-pages-toggle" (click)="toggleSupportingPages()">
            <span class="metric-label">Supporting pages by topic</span>
            <span class="qc-supporting-pages-status" *ngIf="supportingPages.status === 'loading'">
              <span class="qc-loading-spinner-small"></span> Loading…
            </span>
            <span class="qc-supporting-pages-status" *ngIf="supportingPages.status === 'ready'">
              {{ supportingPages.topics.length }} topic{{ supportingPages.topics.length !== 1 ? 's' : '' }}
            </span>
            <span class="qc-dropdown-arrow" [class.expanded]="supportingPages.expanded">&#9660;</span>
          </button>

          <div class="qc-supporting-pages-content" *ngIf="supportingPages.expanded && supportingPages.status === 'ready'">
            <p class="qc-metric-note" *ngIf="supportingPages.topics.length === 0">
              No matching supporting pages found for the top topics on these pages.
            </p>
            <div class="qc-supporting-topic" *ngFor="let topic of supportingPages.topics">
              <div class="qc-supporting-topic-header">
                <span class="qc-topic-pill">{{ topic.topicLabel }}</span>
              </div>
              <div class="qc-supporting-cols">
                <div class="qc-supporting-col">
                  <div class="qc-supporting-col-label">{{ primaryDomainLabel || 'Your site' }}</div>
                  <div class="qc-supporting-page-item" *ngFor="let page of topic.yourPages">
                    <span class="qc-supporting-page-path" [title]="page.url">{{ formatSupportingUrl(page.url) }}</span>
                    <span class="qc-supporting-page-score">{{ page.seoIntentScore | number:'1.2-2' }}</span>
                    <span class="qc-supporting-page-traffic" *ngIf="formatSupportingTraffic(page.estimatedOrganicTraffic)">
                      {{ formatSupportingTraffic(page.estimatedOrganicTraffic) }}
                    </span>
                  </div>
                  <div class="qc-supporting-empty" *ngIf="topic.yourPages.length === 0">No pages found</div>
                </div>
                <div class="qc-supporting-col">
                  <div class="qc-supporting-col-label">{{ compareDomainLabel || 'Compare to' }}</div>
                  <div class="qc-supporting-page-item" *ngFor="let page of topic.theirPages">
                    <span class="qc-supporting-page-path" [title]="page.url">{{ formatSupportingUrl(page.url) }}</span>
                    <span class="qc-supporting-page-score">{{ page.seoIntentScore | number:'1.2-2' }}</span>
                    <span class="qc-supporting-page-traffic" *ngIf="formatSupportingTraffic(page.estimatedOrganicTraffic)">
                      {{ formatSupportingTraffic(page.estimatedOrganicTraffic) }}
                    </span>
                  </div>
                  <div class="qc-supporting-empty" *ngIf="topic.theirPages.length === 0">No pages found</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ng-template #qcFreshnessBlock let-side="side">
      <div class="qc-metric-card qc-freshness-card">
        <div class="metric-label">Freshness</div>
        <div class="qc-freshness-rows">
          <div class="qc-freshness-row">
            <span class="qc-freshness-label">Analyzed:</span>
            <ng-container *ngIf="getAnalyzedAtIsoForSide(side) as analyzedAt; else qcFreshnessNoAnalyzed">
              <span class="qc-freshness-value">{{ analyzedAt | date:'short' }}</span>
            </ng-container>
            <ng-template #qcFreshnessNoAnalyzed>
              <span class="qc-freshness-value">N/A</span>
            </ng-template>
          </div>
          <div class="qc-freshness-row" *ngIf="getLastModifiedIsoForSide(side) as lm">
            <span class="qc-freshness-label">Last modified:</span>
            <span class="qc-freshness-value">{{ lm | date:'short' }}</span>
          </div>
        </div>
      </div>
    </ng-template>

    <ng-template #qcIssuesBlock let-side="side">
      <div class="qc-metric-card qc-issues-card">
        <div class="metric-label">Critical issues</div>
        <div class="metric-value" [class.qc-has-issues]="getCriticalIssuesCountForSide(side) > 0">
          {{ getCriticalIssuesCountForSide(side) }}
        </div>
        <div class="qc-dropdown-section" *ngIf="getCriticalIssuesCountForSide(side) > 0">
          <button type="button" class="qc-dropdown-toggle" (click)="toggleIssuesDetails()">
            <span>{{ isIssuesDetailsExpanded() ? 'Hide' : 'Show' }} details</span>
            <span class="qc-dropdown-arrow" [class.expanded]="isIssuesDetailsExpanded()">&#9660;</span>
          </button>
          <div class="qc-dropdown-content" *ngIf="isIssuesDetailsExpanded()">
            <div class="qc-issue-item" *ngFor="let issue of getCriticalIssuesForSide(side)">{{ issue }}</div>
          </div>
        </div>
      </div>
    </ng-template>

    <ng-template #qcTopicFitBlock let-side="side">
      <div class="qc-metric-card topic-fit-card" *ngIf="topicSignalsCount(analysisForSide(side)) > 0">
        <div class="metric-label">
          Topic Fit
          <span
            class="info-tooltip"
            title="Shows your domain's authority on this page's main topic. Requires domain crawl and topic aggregation."
            >ⓘ</span>
        </div>
        <ng-container *ngIf="topicFitForSide(side) as topicFit">
          <div class="topic-fit-loading" *ngIf="topicFit.loading">
            <span class="qc-loading-spinner-small"></span>
            Loading…
          </div>
          <div class="topic-fit-error" *ngIf="topicFit.error">
            <span class="error-icon">⚠</span>
            Unable to load
          </div>
          <div class="topic-fit-unavailable" *ngIf="!topicFit.loading && !topicFit.error && !topicFit.hasData">
            <span class="muted-text">Domain topics not available yet</span>
            <span
              class="info-tooltip"
              title="Run a full domain crawl and topic aggregation to see this data."
              >ⓘ</span>
          </div>
          <div
            class="topic-fit-not-found"
            *ngIf="!topicFit.loading && !topicFit.error && topicFit.hasData && !topicFit.topicFound"
          >
            <span class="muted-text">Topic not in domain authority</span>
          </div>
          <div
            class="topic-fit-result"
            *ngIf="!topicFit.loading && !topicFit.error && topicFit.hasData && topicFit.topicFound"
          >
            <span class="tier-badge" [ngClass]="topicTierClass(topicFit.tier)">
              {{ formatTopicTierPublic(topicFit.tier) }}
            </span>
            <span class="topic-fit-details">
              on <strong>{{ topicFit.mainTopicLabel }}</strong>
              <span class="authority-score">(Authority {{ topicFit.authorityScore | number:'1.0-0' }})</span>
            </span>
          </div>
        </ng-container>
      </div>
    </ng-template>

    <ng-template #qcIncomingBlock let-side="side">
      <div class="qc-metric-card">
        <div class="metric-label">Incoming Links</div>
        <p class="qc-link-context-note" *ngIf="incomingFor(side).error && !incomingFor(side).expanded">
          {{ incomingFor(side).error }}
        </p>
        <div class="metric-value">{{ getIncomingLinksCount(side) }}</div>
        <div class="qc-link-warning" *ngIf="shouldShowIncomingLinksWarning(side)">
          Full domain crawl recommended for complete incoming links data.
        </div>
        <div class="qc-link-breakdown" *ngIf="getIncomingLinksCount(side) > 0">
          <div class="qc-link-stat">
            <span class="qc-link-type-label" [title]="linkTypeTooltip('contextual')">Contextual</span>
            <strong>{{ getIncomingLinksStats(side).contextual }}</strong>
          </div>
          <div class="qc-link-stat">
            <span class="qc-link-type-label" [title]="linkTypeTooltip('structural')">Structural</span>
            <strong>{{ getIncomingLinksStats(side).structural }}</strong>
          </div>
          <div class="qc-link-stat">
            <span class="qc-link-type-label" [title]="linkTypeTooltip('same_page')">Same-page</span>
            <strong>{{ getIncomingLinksStats(side).samePage }}</strong>
          </div>
          <div class="qc-link-stat">
            <span class="qc-link-type-label" [title]="linkTypeTooltip('unknown')">Unknown</span>
            <strong>{{ getIncomingLinksStats(side).unknown }}</strong>
          </div>
        </div>
        <div class="qc-dropdown-section">
          <button
            type="button"
            class="qc-dropdown-toggle"
            (click)="toggleIncomingExpanded(side)"
            [disabled]="incomingExpandDisabled(side)"
          >
            <span *ngIf="!incomingFor(side).fullLoading">
              {{ isIncomingExpanded(side) ? 'Hide' : 'Show' }} Details
            </span>
            <span *ngIf="incomingFor(side).fullLoading">Loading…</span>
            <span class="qc-dropdown-arrow" [class.expanded]="isIncomingExpanded(side)">▼</span>
          </button>
          <div class="qc-dropdown-content" *ngIf="isIncomingExpanded(side)">
            <div class="qc-link-context-note" *ngIf="incomingFor(side).error">
              Error: {{ incomingFor(side).error }}
            </div>
            <div class="qc-link-context-note" *ngIf="!incomingFor(side).error && getIncomingLinksCount(side) === 0">
              No incoming links found from other pages on this domain.
            </div>
            <div class="qc-link-list" *ngIf="!incomingFor(side).error && getDisplayedIncomingLinks(side).length > 0">
              <div class="qc-link-item" *ngFor="let link of getDisplayedIncomingLinks(side)">
                <div class="qc-link-item-header">
                  <span class="link-type-pill" [ngClass]="linkTypeClass(link.linkType)" [title]="linkTypeTooltip(link.linkType)">
                    {{ linkTypeLabel(link.linkType) }}
                  </span>
                  <span class="qc-link-meta" *ngIf="link.confidence != null">Confidence: {{ (link.confidence * 100) | number:'1.0-0' }}%</span>
                  <span class="qc-link-meta" *ngIf="link.isBroken">Broken</span>
                  <span class="qc-link-meta" *ngIf="link.dofollow === false">nofollow</span>
                </div>
                <div class="qc-link-item-row">
                  <span class="qc-link-item-label">From:</span>
                  <a class="qc-link-item-value qc-link-anchor" [href]="link.sourceUrl" target="_blank" rel="noopener noreferrer">
                    {{ link.sourcePageTitle || link.sourceUrl }}
                  </a>
                </div>
                <div class="qc-link-item-row">
                  <span class="qc-link-item-label">To:</span>
                  <span class="qc-link-item-value">{{ targetUrlForAnalysis(analysisForSide(side)) }}</span>
                </div>
                <div class="qc-link-item-row">
                  <span class="qc-link-item-label">Anchor:</span>
                  <span class="qc-link-item-value">"{{ anchorText(link.anchorText) }}"</span>
                </div>
              </div>
            </div>
            <button
              type="button"
              class="qc-link-show-more"
              *ngIf="hasMoreIncomingLinks(side) || incomingFor(side).showAll"
              (click)="toggleShowAllIncomingLinks(side)"
            >
              {{ incomingFor(side).showAll ? 'Show less' : 'Show all links' }}
            </button>
            <div
              class="qc-link-context-note"
              *ngIf="!incomingFor(side).error && getDisplayedIncomingLinks(side).length === 0 && getIncomingLinksCount(side) > 0"
            >
              No contextual or same-page incoming links to show.
            </div>
          </div>
        </div>
      </div>
    </ng-template>

    <footer class="qc-footer">
      <p class="qc-full-compare-hint">
        For SERP overlap, shared entities, and related pages (extra API usage), continue in Full Compare. Nothing
        runs until you choose <strong>Start Comparison</strong> there.
      </p>
      <button
        type="button"
        class="qc-full-compare-btn btn-secondary"
        (click)="onOpenFullCompareClick()"
        [disabled]="!canOpenFullCompare"
      >
        Deep compare (Topic Share and related pages)
      </button>
    </footer>
  </div>
</div>
